import React from 'react';
import { X, Download, Copy, Search, ZoomIn, ZoomOut, FileText, Eye, ChevronLeft } from 'lucide-react';

interface DocumentCanvasProps {
  isOpen: boolean;
  onClose: () => void;
  content: string;
  title: string;
  metadata?: {
    fileType?: string;
    pages?: number;
    wordCount?: number;
    extractionMethod?: string;
  };
}

export const DocumentCanvas: React.FC<DocumentCanvasProps> = ({
  isOpen,
  onClose,
  content,
  title,
  metadata
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [fontSize, setFontSize] = React.useState(14);
  const [showStats, setShowStats] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
  };

  const handleDownload = () => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/[^a-z0-9]/gi, '_')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const highlightSearchText = (text: string) => {
    if (!searchQuery.trim()) return text;
    
    const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<mark class="bg-yellow-400 text-black px-1 rounded">$1</mark>');
  };

  const getStats = () => {
    const wordCount = content.split(/\s+/).filter(word => word.length > 0).length;
    const charCount = content.length;
    const lineCount = content.split('\n').length;
    return { wordCount, charCount, lineCount };
  };

  const stats = getStats();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-2xl bg-gray-800 border-l border-gray-700 z-50 flex flex-col relative">
      {/* Resize Handle Indicator */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gray-600 hover:bg-blue-500 transition-colors duration-200 cursor-col-resize opacity-0 hover:opacity-100"></div>
      
      {/* Header */}
      <div className="border-b border-gray-700 p-4 flex-shrink-0 bg-gray-800">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3 min-w-0 flex-1">
            <button 
              onClick={onClose}
              className="p-1 hover:bg-gray-700 rounded transition-colors flex-shrink-0"
              title="Close"
            >
              <ChevronLeft className="w-4 h-4 text-gray-400 hover:text-white" />
            </button>
            <div className="w-4 h-4 bg-blue-500 rounded flex-shrink-0"></div>
            <span className="text-sm text-gray-300 truncate font-light">{title}</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-400 flex-shrink-0">
              {metadata?.fileType || 'text'}
            </span>
          </div>
        </div>
        
        {/* Document info */}
        {metadata && (
          <div className="text-xs text-gray-400 mb-3">
            {metadata.pages && `${metadata.pages} pages • `}
            {stats.wordCount.toLocaleString()} words • {stats.charCount.toLocaleString()} chars
            {metadata.extractionMethod && ` • via ${metadata.extractionMethod}`}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center space-x-1">
          <button
            onClick={() => setShowStats(!showStats)}
            className={`px-3 py-1.5 text-xs rounded transition-colors ${
              showStats 
                ? 'bg-gray-700 text-white' 
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
            title="Toggle stats"
          >
            Stats
          </button>
          <button
            onClick={handleCopy}
            className="px-3 py-1.5 text-xs text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
            title="Copy to clipboard"
          >
            Copy
          </button>
          <button
            onClick={handleDownload}
            className="px-3 py-1.5 text-xs text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
            title="Download"
          >
            Download
          </button>
        </div>
      </div>

      {/* Stats Panel */}
      {showStats && (
        <div className="border-b border-gray-700 p-4 bg-gray-800/80">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-lg font-semibold text-blue-400">{stats.wordCount.toLocaleString()}</div>
              <div className="text-xs text-gray-400">Words</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-green-400">{stats.charCount.toLocaleString()}</div>
              <div className="text-xs text-gray-400">Characters</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-purple-400">{stats.lineCount.toLocaleString()}</div>
              <div className="text-xs text-gray-400">Lines</div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Controls */}
      <div className="p-4 border-b border-gray-700 bg-gray-800/50">
        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search in document..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-gray-700 text-white placeholder-gray-400 pl-10 pr-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">Font Size</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFontSize(Math.max(10, fontSize - 1))}
              className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
              title="Decrease font size"
            >
              <ZoomOut className="w-3 h-3" />
            </button>
            <span className="text-xs text-gray-400 min-w-[2ch] text-center">{fontSize}</span>
            <button
              onClick={() => setFontSize(Math.min(24, fontSize + 1))}
              className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
              title="Increase font size"
            >
              <ZoomIn className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 font-mono text-sm bg-gray-900/30">
        <div 
          className="text-gray-300 leading-relaxed whitespace-pre-wrap break-words"
          style={{ fontSize: `${fontSize}px` }}
          dangerouslySetInnerHTML={{ 
            __html: highlightSearchText(content)
              // Add syntax highlighting for common code patterns
              .replace(/import\s+/g, '<span style="color: #7dd3fc">import </span>')
              .replace(/export\s+/g, '<span style="color: #7dd3fc">export </span>')
              .replace(/const\s+/g, '<span style="color: #7dd3fc">const </span>')
              .replace(/function\s+/g, '<span style="color: #fbbf24">function </span>')
              .replace(/return\s+/g, '<span style="color: #f87171">return </span>')
              .replace(/"[^"]*"/g, '<span style="color: #86efac">$&</span>')
              .replace(/\/\*[\s\S]*?\*\//g, '<span style="color: #6b7280">$&</span>')
              .replace(/\/\/.*$/gm, '<span style="color: #6b7280">$&</span>')
          }}
        />
      </div>
    </div>
  );
};
